/**
 * esg.ts
 * ESG page data cache management.
 */

import type {
  PillarsBlock, MetricsBlock, ProductionBlock,
  QualityBlock, TeamBlock, CertificatesBlock,
} from '../esg-defaults';
import { cache, esgInFlight, isStale, notify, ALL_LANGS } from './base';

export interface EsgPageData {
  pillars: PillarsBlock;
  metrics: MetricsBlock;
  production: ProductionBlock;
  quality: QualityBlock;
  team: TeamBlock;
  certs: CertificatesBlock;
}

const LS_ESG_PREFIX = 'sargo_esg_';

interface StoredEsgEntry {
  data: EsgPageData;
  fetchedAt: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function loadEsgFromStorage(lang: string): StoredEsgEntry | null {
  try {
    const raw = localStorage.getItem(`${LS_ESG_PREFIX}${lang}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'fetchedAt' in parsed && 'data' in parsed) {
      return parsed as StoredEsgEntry;
    }
    return null;
  } catch {
    return null;
  }
}

function saveEsgToStorage(lang: string, data: EsgPageData, fetchedAt: number): void {
  try {
    const entry: StoredEsgEntry = { data, fetchedAt };
    localStorage.setItem(`${LS_ESG_PREFIX}${lang}`, JSON.stringify(entry));
  } catch { /* ignore */ }
}

// ─── ESG Page Data ───────────────────────────────────────────────────────────

export function getCachedEsgData(lang: string): EsgPageData | null {
  return cache.esgData.get(lang)?.data ?? null;
}

export async function fetchEsgData(lang: string): Promise<EsgPageData> {
  const entry = cache.esgData.get(lang);
  if (entry && !isStale(entry)) return entry.data;
  if (esgInFlight.has(lang)) return esgInFlight.get(lang)!;

  const promise = (async (): Promise<EsgPageData> => {
    const {
      DEFAULT_PILLARS, DEFAULT_METRICS, DEFAULT_PRODUCTION,
      DEFAULT_QUALITY, DEFAULT_TEAM, DEFAULT_CERTIFICATES,
    } = await import('../esg-defaults');
    const { esgContentDb } = await import('../adminDb');

    const res = await esgContentDb.listByLang(lang);
    if (!res.ok || !Array.isArray(res.data)) {
      const existing = cache.esgData.get(lang)?.data;
      if (existing) return existing;
      return {
        pillars: DEFAULT_PILLARS[lang as 'ru' | 'uz' | 'en'],
        metrics: DEFAULT_METRICS[lang as 'ru' | 'uz' | 'en'],
        production: DEFAULT_PRODUCTION[lang as 'ru' | 'uz' | 'en'],
        quality: DEFAULT_QUALITY[lang as 'ru' | 'uz' | 'en'],
        team: DEFAULT_TEAM[lang as 'ru' | 'uz' | 'en'],
        certs: DEFAULT_CERTIFICATES[lang as 'ru' | 'uz' | 'en'],
      };
    }

    const dbMap: Record<string, any> = {};
    res.data.forEach((block: any) => {
      try {
        const content = JSON.parse(block.contentJson);
        if (content) dbMap[block.blockKey] = content;
      } catch { /* ignore */ }
    });

    const l = lang as 'ru' | 'uz' | 'en';
    const data: EsgPageData = {
      pillars:    { ...DEFAULT_PILLARS[l],    ...(dbMap['pillars']      ?? {}) },
      metrics:    { ...DEFAULT_METRICS[l],    ...(dbMap['metrics']      ?? {}) },
      production: { ...DEFAULT_PRODUCTION[l], ...(dbMap['production']   ?? {}) },
      quality:    { ...DEFAULT_QUALITY[l],    ...(dbMap['quality']      ?? {}) },
      team:       { ...DEFAULT_TEAM[l],       ...(dbMap['team']         ?? {}) },
      certs:      { ...DEFAULT_CERTIFICATES[l],...(dbMap['certificates'] ?? {}) },
    };

    const fetchedAt = Date.now();
    cache.esgData.set(lang, { data, fetchedAt });
    saveEsgToStorage(lang, data, fetchedAt);
    esgInFlight.delete(lang);
    notify();
    return data;
  })().catch((e) => {
    esgInFlight.delete(lang);
    const existing = cache.esgData.get(lang)?.data;
    if (existing) return existing;
    throw e;
  });

  esgInFlight.set(lang, promise);
  return promise;
}

export function invalidateEsgCache(lang?: string) {
  if (lang) {
    cache.esgData.delete(lang);
    esgInFlight.delete(lang);
    try { localStorage.removeItem(`${LS_ESG_PREFIX}${lang}`); } catch { /**/ }
  } else {
    cache.esgData.clear();
    esgInFlight.clear();
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(LS_ESG_PREFIX)) toRemove.push(k);
      }
      toRemove.forEach(k => localStorage.removeItem(k));
    } catch { /**/ }
  }
}

// ─── Initialization ──────────────────────────────────────────────────────────

// (function preloadEsgFromStorage() {
//   for (const lang of ALL_LANGS) {
//     const entry = loadEsgFromStorage(lang);
//     if (entry) {
//       cache.esgData.set(lang, { data: entry.data, fetchedAt: entry.fetchedAt });
//     }
//   }
// })();
